import { PieChartWithPaddingAngleWrapper } from '@modules/charts/components/PieChartWithPaddingAngleWrapper';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { Widgets } from '@modules/common/constants';
import { ChartWidgetErrorMessage } from '@modules/feed/components/ChartWidgetErrorMessage';
import { ShowInQuantifyButton } from '@modules/feed/components/ShowInQuantifyButton';
import {
  useFetchFeedbackSourceDistribution,
  useSearchAnalysesWhereClause,
} from '@modules/feed/hooks';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

export function SourceDistributionWidget() {
  const { sources, error, refetch, loading } =
    useFetchFeedbackSourceDistribution();

  const duration = useSearchStore(({ query }) => query.duration);
  const dispatch = useSearchStore(({ dispatch }) => dispatch);
  const whereClause = useSearchAnalysesWhereClause();

  useEffect(() => {
    dispatch({
      type: SearchActionType.SET_ARE_WIDGETS_LOADING,
      payload: {
        sourceDistributionWidget: loading,
      },
    });

    return () => {
      dispatch({
        type: SearchActionType.SET_ARE_WIDGETS_LOADING,
        payload: {
          sourceDistributionWidget: false,
        },
      });
    };
  }, [dispatch, loading]);

  const renderSourceDistributionPieChart = () => {
    if (error) {
      return (
        <ChartWidgetErrorMessage
          onDataRefresh={() => {
            refetch?.();
          }}
        />
      );
    }

    if (loading) {
      return <CircularLoader />;
    }

    const totalVolume = sources.reduce((sum, source) => sum + source.value, 0);

    return (
      <PieChartWithPaddingAngleWrapper
        data={sources}
        dataKey="value"
        labelKey="label"
        label={{
          subtitle: String(totalVolume),
          caption1: 'Feedback',
          caption2: 'Records',
        }}
        baseWhereClause={whereClause}
        fieldName={FieldNames.Source}
        dateRange={duration as TimeRange}
        minHeight={260}
        formulaID={EQLFunctionName.Count}
        refetch={refetch}
        measuredAsCriteria={null}
        isSingleRecordQuery={false}
        shouldHideOpenInSearchOnFeedbackDrawer
      />
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Stack direction="column" spacing={3}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Typography variant="h6">Source Distribution</Typography>
          {sources?.length > 0 && (
            <ShowInQuantifyButton widget={Widgets.Source} />
          )}
        </Stack>
        <Box sx={{ height: 300 }}>{renderSourceDistributionPieChart()}</Box>
      </Stack>
    </Paper>
  );
}
